//@ts-nocheck
import { add, isSameDay, startOfDay } from 'date-fns';
import {
  getServiceFieldValue,
  getAccommodationDetails
} from '../../../utils/serviceTypeHelper';

/**
 * Creates a checkout service card for accommodations
 * @param {Object} accommodationService - The accommodation service
 * @param {Date} accommodationDayDate - The date of the accommodation's original day
 * @returns {Object} - Checkout service object
 */
export const createCheckoutService = (
  accommodationService,
  accommodationDayDate
) => {
  return {
    tripdetaileddayserviceid: `checkout-${accommodationService?.tripdetaileddayserviceid}`,
    isCheckout: true,
    originalServiceId: accommodationService?.tripdetaileddayserviceid,
    servicetype: {
      ...accommodationService?.servicetype,
      servicetypename: 'Checkout',
      servicetypeicon: 'SignOut'
    },
    servicefields: [
      {
        key: 'TITLE',
        value: getServiceFieldValue(accommodationService, 'TITLE') || '',
        servicefieldid: 'checkout-title'
      },
      {
        key: 'HOTELNAME',
        value: getServiceFieldValue(accommodationService, 'HOTELNAME') || '',
        servicefieldid: 'checkout-hotelname'
      }
    ],

    calculatedCheckOut: getAccommodationDetails(
      accommodationService,
      accommodationDayDate
    )?.checkOutDate
  };
};

/**
 * Checks if checkout should be shown on this day for the given accommodation
 * Similar to shouldShowAccommodationIcon but specifically for checkout day
 *
 * @param {Object} accommodation - The accommodation service
 * @param {Date} currentDayDate - The current day's date
 * @param {Date} accommodationDayDate - The date of the accommodation's original day
 * @returns {boolean} - Whether checkout should be shown
 */
export const shouldShowCheckoutOnDay = (
  accommodation,
  currentDayDate,
  accommodationDayDate
) => {
  if (!currentDayDate || !accommodationDayDate) return false;

  const { checkOutDate } = getAccommodationDetails(
    accommodation,
    accommodationDayDate
  );

  if (!checkOutDate) return false;

  // Normalize dates to avoid timezone issues
  const normalizedCurrentDay = startOfDay(currentDayDate);
  const normalizedCheckOut = startOfDay(checkOutDate);

  // Show checkout if current day IS the checkout date
  return isSameDay(normalizedCurrentDay, normalizedCheckOut);
};

/**
 * Finds accommodations that have checkout on the specified day
 * Similar to processServicesForDay but specifically for checkout services
 *
 * @param {Array} allDetailedDays - All detailed days from the trip
 * @param {Date} currentDayDate - The date to check for checkouts
 * @returns {Array} - Array of checkout services
 */
export const getCheckoutServicesForDay = (
  allDetailedDays = [],
  currentDayDate,
  tripStartDate
) => {
  const checkoutServices = [];

  allDetailedDays?.forEach((detailedDay) => {
    const { tripdetaileddayservice = [], daynumber } = detailedDay;
    const dayDate =
      tripStartDate && add(new Date(tripStartDate), { days: daynumber - 1 });
    const accommodationDayDate = dayDate ? startOfDay(new Date(dayDate)) : null;

    tripdetaileddayservice?.forEach((service) => {
      const { servicetype = {} } = service;

      // Check if this is an accommodation service using code
      const isAccommodation =
        servicetype?.code === 'ACCO' ||
        servicetype?.servicetypename?.toLowerCase() === 'accommodation';

      if (!isAccommodation) return;

      // Check if checkout should be shown on current day
      if (
        accommodationDayDate &&
        shouldShowCheckoutOnDay(service, currentDayDate, accommodationDayDate)
      ) {
        const checkoutService = createCheckoutService(
          service,
          accommodationDayDate
        );
        checkoutServices.push(checkoutService);
      }
    });
  });

  return checkoutServices;
};
